import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { secureGet, secureDelete } from '../../config/api';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';

import { PrimaryButton } from '../../components/PrimaryButton';
import GenericCard from '../Users/GenericCard';
import PageScroller from '../../components/PageScroller';
import DeletePopup from '../../components/DeletePopup';
import Filter from '../../components/Filter';
import OptionsMenu from '../../components/OptionsMenu';
import ModuleWrapper from '../../components/ModuleWrapper';
import SnackbarNotification from '../../components/SnackbarNotification';
import TableLoadingArea from '../../components/TableLoadingArea';

import AddIcon from '../../icons/plusB.svg';
import EditIcon from '../../icons/edit.svg';
import DeleteIcon from '../../icons/trashR.svg';

const AttachementDocumentsType = () => {
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [currentPageCategories, setCurrentPageCategories] = useState([]);
  const [pageSize, setPageSize] = useState(30);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  const [filters, setFilters] = useState({ needle: '' });
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showModule, setShowModule] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { t } = useTranslation();
  const topRef = useRef(null);

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const filterOptions = [
    { name: 'needle', type: 'search' },
  ];

  const paginateData = (data, page, size) => {
    const startIndex = page * size;
    const endIndex = startIndex + size;
    return data.slice(startIndex, endIndex);
  };

  const updatePagination = useCallback((data, page, size) => {
    setTotalPages(Math.ceil((data?.length || 0) / size) || 1);
    setTotalElements(data?.length || 0);
    setCurrentPageCategories(paginateData(data || [], page, size));
  }, []);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await secureGet('/attachment-document-types', showSnackbar, { signal });
      const list = res?.data || [];
      setCategories(list);
      console.log("list", list);
      setFilteredCategories(list);
      setCurrentPage(0);
      updatePagination(list, 0, pageSize);
    } catch (err) {
      if (err.name !== 'AbortError') console.error('Error fetching document categories:', err);
    } finally {
      setLoading(false);
    }
  }, [signal, pageSize, updatePagination]);

  useEffect(() => {
    const ac = new AbortController();
    fetchCategories();
    return () => ac.abort();
  }, [fetchCategories]);

  const applyFilters = useCallback(() => {
    let filtered = [...categories];

    if (filters.needle) {
      const q = filters.needle.toLowerCase();
      filtered = filtered.filter((c) =>
        (c.name || '').toLowerCase().includes(q)
      );
    }

    setFilteredCategories(filtered);
    setCurrentPage(0);
    updatePagination(filtered, 0, pageSize);
  }, [categories, filters, pageSize, updatePagination]);

  useEffect(() => {
    applyFilters();
  }, [filters, categories, applyFilters]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setCurrentPageCategories(paginateData(filteredCategories, page, pageSize));
    topRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(0);
    updatePagination(filteredCategories, 0, newPageSize);
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const resetFilters = () => {
    setFilters({ needle: '' });
    setFilteredCategories(categories);
    updatePagination(categories, 0, pageSize);
  };

  const handleAddClick = () => {
    setSelectedCategory(null);
    setShowModule(true);
  };

  const handleEditClick = (category) => {
    setSelectedCategory(category);
    setShowModule(true);
  };

  const handleDeleteClick = (category) => {
    setItemToDelete(category);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      await secureDelete(`/attachment-document-types/${itemToDelete.id}`, showSnackbar);
      await fetchCategories();
      showSnackbar(t('confirmation.documentCategoriesDelete') || 'Catégorie supprimée', 'success');
    } catch (e) {
      console.error('Error deleting document category:', e);
    } finally {
      setDeleting(false);
      setDeletePopupVisible(false);
      setItemToDelete(null);
    }
  };

  const handleModuleClose = (action = 'cancel') => {
    if (action === 'submit') fetchCategories();
    setShowModule(false);
    setSelectedCategory(null);
  };

  const getMenuOptions = (category) => ([
    { label: t('actions.modify'), icon: EditIcon, action: () => handleEditClick(category) },
    { label: t('actions.delete'), icon: DeleteIcon, color: '#EF476F', action: () => handleDeleteClick(category) },
  ]);

  return (
    <div ref={topRef}>
      <Helmet>
        <title>Drivnest - {t('titles.documentCategories') || 'Gestion des catégories de documents'}</title>
      </Helmet>

      <div className="flex items-center mb-[15px] justify-between">
        <h1 className="text-[40px] font-semibold">{t('titles.documentCategories') || 'Gestion des catégories de documents'}</h1>
        <div className="ml-auto text-[16px]">
          <PrimaryButton
            label={t('primaryButtons.add_documentCategory') || 'Ajouter une catégorie'}
            icon={AddIcon}
            onClick={handleAddClick}
          />
        </div>
      </div>

      <Filter
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        resetFilters={resetFilters}
        filterValues={filters}
      />

      <TableLoadingArea loading={loading} minHeight={200} className="mt-6">
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {!loading && currentPageCategories.map((category) => (
            <GenericCard
              key={category.id}
              title={category.name || ''}
              optionsMenu={
                <div onClick={(e) => e.stopPropagation()}>
                  <OptionsMenu options={getMenuOptions(category)} />
                </div>
              }
            />
          ))}
        </div>

        {!loading && currentPageCategories.length === 0 && (
          <div className="text-center mt-4">
            <p>{t('placeholder.noResults')}</p>
          </div>
        )}
      </TableLoadingArea>

      <PageScroller
        pageNumber={currentPage}
        totalPages={totalPages}
        totalElements={totalElements}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {isDeletePopupVisible && (
        <DeletePopup
          open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
          loading={deleting}
        />
      )}

      {showModule && (
        <ModuleWrapper
          moduleType="attachment-document-types"
          mode={selectedCategory ? 'modify' : 'add'}
          onClose={handleModuleClose}
          onSuccess={(msg) => showSnackbar(msg, 'success')}
          initialValues={selectedCategory || {}}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default AttachementDocumentsType;
